import { createSensor, updateSensor, useFridges, useSensor, useSensorForm } from '@gozoki/api';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { Button, IconButton, Typography } from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import LoadingPaper from '../../components/containers/LoadingPaper';
import SelectRhf from '../../components/inputs/SelectRhf';
import TextFieldRhf from '../../components/inputs/TextFieldRhf';
import Page from '../../components/Page';
import { ROUTES } from '../../utils/constants/routes.constants';
import { ThemeColors } from '../../utils/theme/colors.theme';
import { fonts } from '../../utils/theme/fonts.theme';
import { ButtonOverrides } from '../../utils/theme/overrides.theme';

const AddSensorPage = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const queryClient = useQueryClient();
    const { id } = useParams();
    const updateMode = id !== undefined; // Update mode if there is a sensor ID in the URL

    // If this is update mode, use isLoading to show a loading indicator before the data arrives
    const { data, isLoading } = useSensor(parseInt(id ?? '', 10));

    const { control, trigger, getValues, setValue } = useSensorForm();

    const { data: fridges = [] } = useFridges();

    // Fill the form with initial data once the data is fetched
    useEffect(() => {
        // Set all values
        if (data) {
            setValue('reference', data.reference);
            setValue('label', data.label);
            setValue('description', data.description || '');
            setValue('model', data.model);
            setValue('fridgeReference', data.fridge?.reference || '');
        }
    }, [setValue, data]);

    // Preserve tab parameter when navigating back
    const navigateBack = () => {
        const searchParams = new URLSearchParams(location.search);
        const tab = searchParams.get('tab');
        const url = tab ? `${ROUTES.SENSORS}?tab=${tab}` : ROUTES.SENSORS;
        navigate(url);
    };

    // ******************************************************************** //
    //                              MUTATIONS                               //
    // ******************************************************************** //

    const { mutate: upload, isPending: isUploading } = useMutation({
        mutationFn: createSensor,
        onSuccess: () => {
            // Invalidate all sensors queries (for all store filters)
            queryClient.invalidateQueries({ queryKey: ['sensors'] });
            navigateBack();
        },
    });

    const { mutate: update, isPending: isUpdating } = useMutation({
        mutationFn: updateSensor,
        onSuccess: () => {
            // Invalidate all sensors queries and the specific sensor query
            queryClient.invalidateQueries({ queryKey: ['sensors'] });
            queryClient.invalidateQueries({ queryKey: ['sensor', parseInt(id ?? '', 10)] });
            navigateBack();
        },
    });

    // ******************************************************************** //
    //                               HANDLERS                               //
    // ******************************************************************** //

    const uploadSensor = async () => {
        const valid = await trigger(); // Trigger form validation

        if (valid) {
            upload(getValues());
        }
    };

    const handleUpdateSensor = async () => {
        const valid = await trigger(); // Trigger form validation

        if (valid) {
            update({
                id: parseInt(id!, 10),
                data: getValues(),
            });
        }
    };

    return (
        <Page sx={{ maxWidth: '50vw', margin: 'auto' }}>
            <div style={{ display: 'flex', alignItems: 'center' }}>
                <IconButton onClick={navigateBack}>
                    <ArrowBackIcon
                        sx={{
                            color: ThemeColors.BLACK,
                            height: '24px',
                            width: '24px',
                        }}
                    />
                </IconButton>
                <Typography style={{ ...fonts.pageSubtitle, marginLeft: '16px' }} flexGrow={1}>
                    {updateMode ? 'Modifier un capteur' : 'Ajouter un capteur'}
                </Typography>

                <Button
                    variant="outlined"
                    sx={{ marginRight: '16px', ...ButtonOverrides.blackOutlined }}
                    onClick={navigateBack}
                >
                    Annuler
                </Button>
                <Button
                    variant="contained"
                    disabled={isUpdating || isUploading}
                    onClick={updateMode ? handleUpdateSensor : uploadSensor}
                >
                    {updateMode ? 'Modifier' : 'Ajouter'}
                </Button>
            </div>
            <LoadingPaper
                sx={{ marginTop: '40px', padding: '24px', paddingBottom: '40px' }}
                loading={isLoading && updateMode}
            >
                <Typography mb="8px" style={fonts.inputHeader}>
                    Référence (devEUI) *
                </Typography>
                <TextFieldRhf
                    variant="outlined"
                    placeholder="Référence (devEUI)"
                    color="info"
                    control={control}
                    trigger={trigger}
                    name="reference"
                    fullWidth
                    required
                />
                <Typography mb="8px" mt="24px" style={fonts.inputHeader}>
                    Libellé *
                </Typography>
                <TextFieldRhf
                    variant="outlined"
                    placeholder="Libellé du capteur"
                    color="info"
                    control={control}
                    trigger={trigger}
                    name="label"
                    fullWidth
                />
                <Typography mb="8px" mt="24px" style={fonts.inputHeader}>
                    Description
                </Typography>
                <TextFieldRhf
                    variant="outlined"
                    placeholder="Description (optionnel)"
                    color="info"
                    control={control}
                    trigger={trigger}
                    name="description"
                    fullWidth
                    multiline
                    rows={3}
                />
                <Typography mb="8px" mt="24px" style={fonts.inputHeader}>
                    Modèle *
                </Typography>
                <TextFieldRhf
                    variant="outlined"
                    placeholder="Modèle du capteur"
                    color="info"
                    control={control}
                    trigger={trigger}
                    name="model"
                    fullWidth
                />
                <Typography mb="8px" mt="24px" style={fonts.inputHeader}>
                    Réfrigérateur *
                </Typography>
                <SelectRhf
                    control={control}
                    name="fridgeReference"
                    options={fridges.map((fridge) => ({
                        value: fridge.reference,
                        label: `${fridge.label} (${fridge.type})${
                            fridge.store ? ` - ${fridge.store.label}` : ''
                        }`,
                    }))}
                />
            </LoadingPaper>
        </Page>
    );
};

export default AddSensorPage;
